import React from 'react';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';

export interface ContactCardProps {
  name: string;
  title: string;
  role: string;
  roleColor?: 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink' | 'gray';
  imageUrl: string;
  email: string;
  phone: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
}

export default function ContactCard({
  name,
  title,
  role,
  roleColor = 'green',
  imageUrl,
  email,
  phone,
  maxWidth = 'sm',
  className = ''
}: ContactCardProps) {
  const getRoleColorClass = (color: string) => {
    switch (color) {
      case 'red':
        return 'bg-red-50 text-red-700 inset-ring inset-ring-red-600/20';
      case 'yellow':
        return 'bg-yellow-50 text-yellow-700 inset-ring inset-ring-yellow-600/20';
      case 'green':
        return 'bg-green-50 text-green-700 inset-ring inset-ring-green-600/20';
      case 'blue':
        return 'bg-blue-50 text-blue-700 inset-ring inset-ring-blue-600/20';
      case 'indigo':
        return 'bg-indigo-50 text-indigo-700 inset-ring inset-ring-indigo-600/20';
      case 'purple':
        return 'bg-purple-50 text-purple-700 inset-ring inset-ring-purple-600/20';
      case 'pink':
        return 'bg-pink-50 text-pink-700 inset-ring inset-ring-pink-600/20';
      case 'gray':
      default:
        return 'bg-gray-50 text-gray-700 inset-ring inset-ring-gray-600/20';
    }
  };

  const getMaxWidthClass = (width: string) => {
    switch (width) {
      case 'xs':
        return 'max-w-xs';
      case 'sm':
        return 'max-w-sm';
      case 'md':
        return 'max-w-md';
      case 'lg':
        return 'max-w-lg';
      case 'xl':
        return 'max-w-xl';
      case 'full':
        return 'max-w-full';
      default:
        return 'max-w-sm';
    }
  };

  const widthClass = getMaxWidthClass(maxWidth);

  return (
    <li className={`flex flex-col divide-y divide-gray-200 dark:divide-gray-700 rounded-lg bg-white dark:bg-gray-800 text-center shadow-sm ring-1 ring-gray-200 dark:ring-gray-700 ${widthClass} ${className}`}>
      <div className="flex flex-1 flex-col p-8">
        <img
          src={imageUrl}
          alt={name}
          className="mx-auto size-32 shrink-0 rounded-full bg-gray-300 dark:bg-gray-600 outline -outline-offset-1 outline-black/5"
        />
        <h3 className="mt-6 text-sm font-medium text-heading">{name}</h3>
        <dl className="mt-1 flex grow flex-col justify-between">
          <dt className="sr-only">Title</dt>
          <dd className="text-sm text-muted">{title}</dd>
          <dt className="sr-only">Role</dt>
          <dd className="mt-3">
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getRoleColorClass(roleColor)}`}>
              {role}
            </span>
          </dd>
        </dl>
      </div>
      <div>
        <div className="-mt-px flex divide-x divide-gray-200 dark:divide-gray-700">
          <div className="flex w-0 flex-1">
            <a
              href={`mailto:${email}`}
              className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-heading hover:bg-highlight"
            >
              <EnvelopeIcon className="size-5 text-gray-400" aria-hidden="true" />
              Email
            </a>
          </div>
          <div className="-ml-px flex w-0 flex-1">
            <a
              href={`tel:${phone}`}
              className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-heading hover:bg-highlight"
            >
              <PhoneIcon className="size-5 text-gray-400" aria-hidden="true" />
              Call
            </a>
          </div>
        </div>
      </div>
    </li>
  );
}
